'use client'

import { useState, useEffect, useCallback } from 'react'
import { Application, Property, User } from '@/types'
import ChatInterface from './ChatInterface'
// import PaymentInterface from './PaymentInterface' // Unused import
import { useCurrency } from '@/lib/currency'
import EmptyState from '@/components/EmptyState'
import { TableSkeleton } from '@/components/SkeletonLoader'
import { getApiUrl } from '@/lib/apiUrl'
// import { loadStripe } from '@stripe/stripe-js' // Unused import

interface ApplicationWithDetails extends Application {
  property: Property & {
    landlord: User
  }
  client: User
}

interface ApplicationStatusProps {
  userId: string
}

export default function ApplicationStatus({ userId }: ApplicationStatusProps) {
  const [applications, setApplications] = useState<ApplicationWithDetails[]>([])
  const { format } = useCurrency()
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all')
  const [showChat, setShowChat] = useState(false)
  const [showPayment, setShowPayment] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<ApplicationWithDetails | null>(null)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [paymentStatuses, setPaymentStatuses] = useState<Record<string, 'pending' | 'completed' | 'not_found'>>({})
  const [escrowStatuses, setEscrowStatuses] = useState<Record<string, string | null>>({})

  // Load user's applications
  const loadApplications = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      
      if (!token) {
        console.error('No auth token found')
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/applications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      
      if (!response.ok) {
        console.error('Error loading applications:', response.statusText)
        return
      }

      const data = await response.json()
      // Handle both array and object with applications property
      const applicationsArray = Array.isArray(data) ? data : (data.applications || [])
      // Transform applications to match frontend format
      const transformedApplications = applicationsArray.map((app: any) => {
        // Transform landlord data if present
        const landlord = app.landlord || app.property?.landlord
        const transformedLandlord = landlord ? {
          ...landlord,
          id: landlord._id || landlord.id,
          firstName: landlord.firstName || landlord.kyc_data?.firstName || '',
          lastName: landlord.lastName || landlord.kyc_data?.lastName || '',
          email: landlord.email || ''
        } : null

        // Transform property data if present
        const property = app.property ? {
          ...app.property,
          id: app.property._id || app.property.id,
          landlord: transformedLandlord || app.property.landlord
        } : app.property

        return {
          ...app,
          id: app._id || app.id,
          property_id: app.property?._id || app.property?.id || app.property_id || app.property,
          created_at: app.createdAt || app.applicationDate || app.created_at,
          property: property,
          landlord: transformedLandlord || app.landlord
        }
      })
      setApplications(transformedApplications)

      // Check payment status for each approved application
      const paymentChecks = transformedApplications
        .filter((app: any) => app.status === 'approved' || app.status === 'accepted')
        .map(async (app: any) => {
          try {
            const token = localStorage.getItem('authToken')
            if (!token) {
              return { appId: app.id, status: 'not_found' as const }
            }

            // Check payment history for this application
            const apiUrl = getApiUrl()
            const paymentResponse = await fetch(`${apiUrl}/payments/history?applicationId=${app.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            })
            
            if (paymentResponse.ok) {
              const paymentData = await paymentResponse.json()
              // Check if there's a completed payment
              const payments = paymentData.payments || paymentData || []
              const hasCompletedPayment = Array.isArray(payments) && payments.some((p: any) => p.status === 'completed' || p.status === 'succeeded')
              const escrowStatus = Array.isArray(payments) && hasCompletedPayment 
                ? payments.find((p: any) => p.status === 'completed' || p.status === 'succeeded')?.escrowStatus || null
                : null
              return { 
                appId: app.id, 
                status: hasCompletedPayment ? 'completed' : 'not_found' as const,
                escrowStatus: escrowStatus
              }
            } else {
              return { appId: app.id, status: 'not_found' as const }
            }
          } catch (error) {
            console.error('Error checking payment status:', error)
            return { appId: app.id, status: 'not_found' as const }
          }
        }) || []

      const paymentResults = await Promise.all(paymentChecks)
      const paymentStatusMap: Record<string, 'pending' | 'completed' | 'not_found'> = {}
      const escrowStatusMap: Record<string, string | null> = {}
      paymentResults.forEach(result => {
        paymentStatusMap[result.appId] = result.status
        if ((result as any).escrowStatus) {
          escrowStatusMap[result.appId] = (result as any).escrowStatus
        }
      })
      setPaymentStatuses(paymentStatusMap)
      setEscrowStatuses(escrowStatusMap)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }, [userId])

  // Load current user
  const loadCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken')
      
      if (!token) {
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      
      if (response.ok) {
        const userProfile = await response.json()
        setCurrentUser(userProfile)
      }
    } catch (error) {
      console.error('Error loading current user:', error)
    }
  }

  useEffect(() => {
    loadApplications()
    loadCurrentUser()
  }, [userId, loadApplications])

  // Filter applications based on status
  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true
    // Handle both 'accepted' and 'approved' statuses
    if (filter === 'accepted') {
      return app.status === 'accepted' || app.status === 'approved'
    }
    return app.status === filter
  })

  // Get status badge color (matches landlord dashboard palette)
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-800'
      case 'accepted':
        return 'bg-emerald-100 text-emerald-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-stone-100 text-stone-800'
    }
  }

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏳'
      case 'accepted':
        return '✅'
      case 'rejected':
        return '❌'
      default:
        return '📋'
    }
  }

  // Format price via currency context
  const formatPrice = (price: number) => format(price)

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Get landlord display name
  const getLandlordDisplayName = (landlord: User | null | undefined) => {
    if (!landlord) return 'Unknown Landlord'
    
    // Check multiple possible sources for name
    const firstName = landlord.firstName || landlord.kyc_data?.firstName || ''
    const lastName = landlord.lastName || landlord.kyc_data?.lastName || ''
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    }
    if (firstName) {
      return firstName
    }
    if (lastName) {
      return lastName
    }
    
    // Fallback to email if no name available
    return landlord.email || 'Unknown Landlord'
  }



  // Handle chat button click
  const handleChatClick = (application: ApplicationWithDetails) => {
    setSelectedApplication(application)
    setShowChat(true)
  }

  // Handle payment button click
  const handlePaymentClick = (application: ApplicationWithDetails) => {
    setSelectedApplication(application)
    setShowPayment(true)
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 sm:p-5">
              <div className="h-4 bg-stone-200 rounded mb-2 animate-pulse"></div>
              <div className="h-8 bg-stone-200 rounded w-1/2 animate-pulse"></div>
            </div>
          ))}
        </div>
        {/* Table Skeleton */}
        <TableSkeleton rows={5} columns={5} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-display text-stone-900">My Applications</h2>
          <p className="text-stone-600 mt-0.5 sm:mt-1 text-sm sm:text-base">
            Track your rental applications and their status
          </p>
        </div>
        <button
          onClick={loadApplications}
          className="w-full sm:w-auto px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25 font-semibold text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 sm:p-5 hover:shadow-md transition-shadow">
          <h3 className="text-xs sm:text-sm font-medium text-stone-500">Total Applications</h3>
          <p className="text-xl sm:text-2xl font-display font-bold text-stone-900 mt-1">{applications.length}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 sm:p-5 hover:shadow-md transition-shadow">
          <h3 className="text-xs sm:text-sm font-medium text-stone-500">Pending Review</h3>
          <p className="text-xl sm:text-2xl font-display font-bold text-amber-600 mt-1">
            {applications.filter(a => a.status === 'pending').length}
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 sm:p-5 hover:shadow-md transition-shadow">
          <h3 className="text-xs sm:text-sm font-medium text-stone-500">Accepted</h3>
          <p className="text-xl sm:text-2xl font-display font-bold text-emerald-600 mt-1">
            {applications.filter(a => a.status === 'accepted' || a.status === 'approved').length}
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 sm:p-5 hover:shadow-md transition-shadow">
          <h3 className="text-xs sm:text-sm font-medium text-stone-500">Rejected</h3>
          <p className="text-xl sm:text-2xl font-display font-bold text-red-600 mt-1">
            {applications.filter(a => a.status === 'rejected').length}
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 sm:p-5">
        <div className="flex flex-wrap gap-4">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-stone-700 mb-1">Status Filter</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'pending' | 'accepted' | 'rejected')}
              className="w-full sm:w-auto px-3 py-2.5 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-stone-900 bg-white"
            >
              <option value="all">All Applications ({applications.length})</option>
              <option value="pending">Pending ({applications.filter(a => a.status === 'pending').length})</option>
              <option value="accepted">Accepted ({applications.filter(a => a.status === 'accepted').length})</option>
              <option value="rejected">Rejected ({applications.filter(a => a.status === 'rejected').length})</option>
            </select>
          </div>
        </div>
      </div>

      {/* Applications List */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
        {filteredApplications.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-stone-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Property
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Landlord
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Applied
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-stone-200">
                {filteredApplications.map((application) => (
                  <tr key={application.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-stone-900 break-words">
                          {application.property?.title}
                        </div>
                        <div className="text-sm text-stone-500">{application.property?.location}</div>
                        <div className="text-sm text-emerald-600 font-medium">
                          {(() => {
                            // For short-term rentals, show calculated total (e.g., "#112 for 6 days")
                            if (application.property?.rentalType === 'short-term' && (application as any).total_amount) {
                              const days = (application as any).bookingDates?.start && (application as any).bookingDates?.end
                                ? Math.ceil((new Date((application as any).bookingDates.end).getTime() - new Date((application as any).bookingDates.start).getTime()) / (1000 * 60 * 60 * 24))
                                : (application as any).lease_duration || 1
                              return `${formatPrice((application as any).total_amount)} for ${days} ${days === 1 ? 'day' : 'days'}`
                            }
                            // For long-term, show monthly rate
                            return `${formatPrice(application.property?.price || 0)}/month`
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-stone-900">
                        {getLandlordDisplayName((application as any).landlord || application.property?.landlord)}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-lg mr-2">{getStatusIcon(application.status)}</span>
                        <div className="text-right">
                          <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${getStatusColor(application.status)}`}>
                            {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                          </span>
                          {application.status === 'rejected' && (
                            <div className="text-xs text-stone-500 mt-1">
                              Chat unavailable
                            </div>
                          )}
                          {/* Payment Status Indicator for Accepted/Approved Applications */}
                          {(application.status === 'accepted' || application.status === 'approved') && (
                            <div className="text-xs mt-1 space-y-1">
                              {(() => {
                                const paymentStatus = paymentStatuses[application.id]
                                const escrowStatus = escrowStatuses[application.id]
                                if (paymentStatus === 'completed') {
                                  if (escrowStatus === 'held') {
                                    return (
                                      <div className="text-emerald-600">✅ Payment Completed</div>
                                    )
                                  }
                                  return <span className="text-emerald-600">✅ Payment Completed</span>
                                }
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                      {formatDate(application.created_at)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                        {/* Chat button - available for all applications */}
                        {application.status !== 'rejected' ? (
                          (() => {
                            // Check if chat should be available based on status and payment
                            if (application.status === 'pending') {
                            return (
                              <span className="text-stone-400 px-2 py-1 rounded-xl border border-stone-200 text-center" title="Chat available after application is accepted">
                                  <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3.13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                  </svg>
                                </span>
                              )
                            }
                            
                            if (application.status === 'accepted' || application.status === 'approved') {
                              // Chat is available once application is accepted (no need to wait for payment)
                              return (
                                <button
                                  onClick={() => handleChatClick(application)}
                                  className="text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-xl border border-emerald-200 hover:bg-emerald-50 text-center transition-colors"
                                  title="Chat with landlord"
                                >
                                  <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3.13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                  </svg>
                                </button>
                              )
                            }
                            
                            // Default case - should not happen
                            return (
                              <button
                                onClick={() => handleChatClick(application)}
                                className="text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-xl border border-emerald-200 hover:bg-emerald-50 text-center transition-colors"
                                title="Chat with landlord"
                              >
                                <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3.13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                              </button>
                            )
                          })()
                        ) : (
                          <span className="text-stone-400 px-2 py-1 rounded-xl border border-stone-200 text-center" title="Chat unavailable for rejected applications">
                            <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3.13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </span>
                        )}
                        
                        {/* Payment status - show "Paid" if payment completed, otherwise show "Pay" button */}
                        {(application.status === 'approved' || application.status === 'accepted') && (
                          (() => {
                            const paymentStatus = paymentStatuses[application.id]
                            // Show "Paid" status if payment is completed
                            if (paymentStatus === 'completed') {
                              return (
                                <div className="flex items-center gap-2">
                                  <span className="text-emerald-600 font-medium text-sm">✅ Paid</span>
                                </div>
                              )
                            }
                            // Show payment button if payment is not completed
                            return (
                              <button
                                onClick={() => handlePaymentClick(application)}
                                className="text-emerald-600 hover:text-emerald-700 px-3 py-1.5 rounded-xl border border-emerald-200 hover:bg-emerald-50 text-center font-medium text-sm flex items-center gap-1.5 transition-colors"
                                title="Make payment for this property"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                                <span>Pay</span>
                              </button>
                            )
                          })()
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={
              <svg className="w-16 h-16 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            title={filter === 'all' ? 'No Applications Yet' : `No ${filter.charAt(0).toUpperCase() + filter.slice(1)} Applications`}
            description={
              filter === 'all' 
                ? "You haven't applied to any properties yet. Browse available properties and submit your first application!"
                : `You don't have any ${filter} applications. ${filter === 'pending' ? 'Applications will appear here once submitted.' : `Try adjusting your filter to see ${filter === 'accepted' ? 'accepted' : 'rejected'} applications.`}`
            }
            action={
              filter === 'all' 
                ? {
                    label: 'Browse Properties',
                    href: '/dashboard/client'
                  }
                : {
                    label: 'View All Applications',
                    onClick: () => setFilter('all')
                  }
            }
          />
        )}
      </div>

      {/* Chat Modal */}
      {showChat && selectedApplication && currentUser && (
        <ChatInterface
          application={selectedApplication}
          currentUser={currentUser}
          onClose={() => {
            setShowChat(false)
            setSelectedApplication(null)
          }}
        />
      )}

      {/* Payment Modal */}
      {showPayment && selectedApplication && currentUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-stone-200 max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-display font-semibold text-stone-900">Make Payment</h2>
              <button
                onClick={() => {
                  setShowPayment(false)
                  setSelectedApplication(null)
                }}
                className="text-stone-400 hover:text-stone-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {selectedApplication.property && (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-stone-600">Property</p>
                  <p className="text-lg font-medium text-stone-900">{selectedApplication.property.title}</p>
                </div>
                
                <div>
                  <p className="text-sm text-stone-600">Amount</p>
                  <p className="text-2xl font-display font-bold text-emerald-600">
                    {format(selectedApplication.property.price || 0)}
                    {selectedApplication.property.rentalType === 'short-term' ? '/day' : '/month'}
                  </p>
                </div>

                {(selectedApplication as any).total_amount && selectedApplication.property.rentalType === 'short-term' && (
                  <div>
                    <p className="text-sm text-stone-600">Total Amount</p>
                    <p className="text-xl font-semibold text-stone-900">
                      {format((selectedApplication as any).total_amount || (selectedApplication as any).bookingDates?.totalAmount || 0)}
                    </p>
                  </div>
                )}

                <button
                  className="w-full bg-emerald-600 text-white px-4 py-3 rounded-xl hover:bg-emerald-700 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25 font-semibold"
                  onClick={async () => {
                    try {
                      if (!selectedApplication.property) {
                        alert('Property information not found')
                        return
                      }

                      const token = localStorage.getItem('authToken')
                      if (!token) {
                        alert('Please log in to make a payment')
                        return
                      }

                      // Calculate amount - use total_amount for short-term, property.price for long-term
                      const amount = selectedApplication.property.rentalType === 'short-term' 
                        ? ((selectedApplication as any).total_amount || (selectedApplication as any).bookingDates?.totalAmount || selectedApplication.property.price)
                        : (selectedApplication.property.price || 0)

                      // Validate amount
                      if (!amount || amount <= 0) {
                        alert('Invalid payment amount. Please contact support.')
                        console.error('Invalid amount:', amount)
                        return
                      }

                      // Create Stripe checkout session
                      const apiUrl = getApiUrl()
                      let res: Response
                      try {
                        res = await fetch(`${apiUrl}/payments/create-checkout`, {
                          method: 'POST',
                          headers: { 
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            applicationId: selectedApplication.id,
                            amount: amount,
                            currency: (localStorage.getItem('landlordnoagent:currency') === 'NGN' ? 'ngn' : 'ngn'),
                          })
                        })
                      } catch (fetchError: any) {
                        console.error('Network error creating payment session:', fetchError)
                        alert(`Network error: ${fetchError.message || 'Unable to connect to payment server. Please check your internet connection and try again.'}`)
                        return
                      }

                      if (!res.ok) {
                        let errorData: any = { message: 'Failed to create payment session' }
                        try {
                          const contentType = res.headers.get('content-type')
                          if (contentType && contentType.includes('application/json')) {
                            errorData = await res.json()
                          } else {
                            const text = await res.text()
                            console.error('Non-JSON error response:', text.substring(0, 500))
                            errorData.message = `Server error (${res.status}): ${text.substring(0, 100)}`
                          }
                        } catch (parseError) {
                          console.error('Error parsing error response:', parseError)
                          errorData.message = `Server error (${res.status} ${res.statusText}). Please try again or contact support.`
                        }
                        alert(errorData.message || 'Failed to create payment session. Please try again or contact support.')
                        console.error('Stripe session error:', errorData)
                        return
                      }

                      const data = await res.json()
                      
                      // Log session creation for debugging
                      console.log('✅ Stripe session created:', {
                        sessionId: data.sessionId,
                        publishableKeyHint: data.publishableKeyHint
                      })

                      // Check if we have a sessionId or url
                      if (!data.sessionId && !data.url) {
                        alert('Invalid payment session response. Please try again.')
                        console.error('Invalid payment session data:', data)
                        return
                      }

                      // ALWAYS use direct URL redirect - this avoids all key mismatch issues
                      // The checkout URL works regardless of publishable key configuration
                      if (data.url) {
                        console.log('✅ Redirecting to Stripe checkout URL:', data.url)
                        window.location.href = data.url
                        return
                      }
                      
                      // If no URL, something is wrong with the backend response
                      console.error('❌ No checkout URL in response:', data)
                      alert('Payment session created but no checkout URL available. Please contact support.')
                    } catch (error: any) {
                      console.error('Payment error:', error)
                      
                      // Provide more specific error messages
                      let errorMessage = 'Payment could not be started'
                      
                      if (error instanceof TypeError && error.message.includes('fetch')) {
                        errorMessage = 'Network error: Unable to connect to payment server. Please check your internet connection and try again.'
                      } else if (error.message) {
                        errorMessage = `Payment error: ${error.message}`
                      } else if (typeof error === 'string') {
                        errorMessage = `Payment error: ${error}`
                      }
                      
                      alert(errorMessage)
                    }
                  }}
                >
                  Proceed to Payment
                </button>

                <button
                  onClick={() => {
                    setShowPayment(false)
                    setSelectedApplication(null)
                  }}
                  className="w-full bg-stone-100 text-stone-700 px-4 py-2.5 rounded-xl hover:bg-stone-200 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
} 

